import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import {
  Plus,
  FileText,
  Pencil,
  Link2,
  Ban,
  Trash2,
  ExternalLink,
  ClipboardList,
  Check,
  Send,
  ShieldCheck,
  Undo2,
  Mail,
} from 'lucide-react';
import { db } from '../../utils/firebase';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import type { ChangeOrder, ProposalBuilderData } from '../../types';
import { getSignedCommercialListTotal } from '../../utils/proposalPublicGrandTotals';
import {
  sumApprovedChangeOrders,
  recomputeAndSyncContractValue,
  contractBaseForDeal,
  priorContractValueForChangeOrder,
} from '../../utils/contractValue';
import {
  subscribeChangeOrdersForDeal,
  updateChangeOrder,
  deleteChangeOrder,
  getChangeOrderReference,
  retractChangeOrder,
} from '../../utils/changeOrders';
import type { ChangeOrderReviewSendMode } from '../proposals/ChangeOrderReviewModal';
import { ensureChangeOrderSalesOrder, approveChangeOrderInternally } from '../../utils/changeOrderSync';
import { ensureBaseProposalSalesOrder } from '../../utils/signedDealSync';
import { canApproveChangeOrderInternally } from '../../utils/userRoles';
import ChangeOrderEditor from '../proposals/ChangeOrderEditor';
import ChangeOrderReviewModal from '../proposals/ChangeOrderReviewModal';
import ChangeOrderInternalBadge, { isInternallyApprovedChangeOrder } from '../proposals/ChangeOrderInternalBadge';
import '../proposals/ChangeOrder.css';

const FUNCTIONS_URL =
  process.env.REACT_APP_FIREBASE_FUNCTIONS_URL ||
  'https://us-central1-lctf-projects.cloudfunctions.net';

const fmt = (n: number) =>
  `${n < 0 ? '-' : ''}$${Math.abs(Math.round(n)).toLocaleString('en-US')}`;

interface DealChangeOrderPanelProps {
  dealId: string;
  projectId?: string;
  clientName?: string;
  projectName?: string;
  projectNumber?: string;
}

export default function DealChangeOrderPanel({
  dealId,
  projectId,
  clientName,
  projectName,
  projectNumber,
}: DealChangeOrderPanelProps) {
  const { user } = useAuth();
  const {
    updateEnhancedDeal,
    ensureProcurementTaskGroupForProject,
    enhancedDeals,
    contacts,
    createActivity,
  } = useCRM();
  const procurementEnsuredRef = useRef<Set<string>>(new Set());
  const salesOrderEnsuredRef = useRef<Set<string>>(new Set());
  const baseSalesOrderEnsuredRef = useRef(false);
  const canInternalApprove = canApproveChangeOrderInternally(user?.role);

  const [changeOrders, setChangeOrders] = useState<ChangeOrder[]>([]);
  const [signedBase, setSignedBase] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ChangeOrder | null | undefined>(undefined); // undefined = closed, null = new
  const [feedback, setFeedback] = useState<string | null>(null);

  // Review-and-send modal state (mirrors the proposal send flow).
  const [reviewCo, setReviewCo] = useState<ChangeOrder | null>(null);
  const [reviewSendMode, setReviewSendMode] = useState<ChangeOrderReviewSendMode>('release');
  const [sendingCo, setSendingCo] = useState(false);
  const [approvingCoId, setApprovingCoId] = useState<string | null>(null);
  const [coMessage, setCoMessage] = useState('');
  const [coRecipients, setCoRecipients] = useState<string[]>([]);

  // Best-guess default recipient: the deal's primary contact email.
  const defaultClientEmail = useMemo(() => {
    const deal = enhancedDeals.find(d => d.id === dealId);
    if (!deal?.contactId) return '';
    return contacts.find(c => c.id === deal.contactId)?.email || '';
  }, [enhancedDeals, contacts, dealId]);

  useEffect(() => {
    if (!dealId) return;
    setLoading(true);
    const unsub = subscribeChangeOrdersForDeal(dealId, orders => {
      setChangeOrders(orders);
      setLoading(false);
    });
    return unsub;
  }, [dealId]);

  useEffect(() => {
    if (!dealId) return;
    let cancelled = false;
    getDoc(doc(db, 'proposalBuilders', dealId))
      .then(snap => {
        if (cancelled) return;
        setSignedBase(snap.exists() ? getSignedCommercialListTotal(snap.data() as ProposalBuilderData) : null);
      })
      .catch(() => !cancelled && setSignedBase(null));
    return () => {
      cancelled = true;
    };
  }, [dealId]);

  // Backfill the original contract sales order when COs exist but the base
  // proposal order was never created (e.g. internal sign after CO approval).
  useEffect(() => {
    if (!dealId || signedBase == null) return;
    if (baseSalesOrderEnsuredRef.current) return;
    baseSalesOrderEnsuredRef.current = true;
    ensureBaseProposalSalesOrder(dealId)
      .catch(err => {
        baseSalesOrderEnsuredRef.current = false;
        console.warn('[ChangeOrders] base proposal sales order backfill failed', err);
      });
  }, [dealId, signedBase]);

  // Lightweight purchasing: make sure approved change orders have a Procurement
  // task group home for follow-on ordering. The public sign route can't reach
  // CRM context, so staff viewing the hub provisions it (idempotent).
  useEffect(() => {
    if (!projectId) return;
    const hasApproved = changeOrders.some(co => co.status === 'approved');
    if (!hasApproved || procurementEnsuredRef.current.has(projectId)) return;
    procurementEnsuredRef.current.add(projectId);
    ensureProcurementTaskGroupForProject(projectId).catch(err =>
      console.warn('[ChangeOrders] ensure procurement group failed', err),
    );
  }, [changeOrders, projectId, ensureProcurementTaskGroupForProject]);

  // Approved change orders: backfill sales order if missing (older COs). New
  // approvals create the order server-side in onChangeOrderApproved only.
  useEffect(() => {
    changeOrders.forEach(co => {
      if (!co.id) return;
      if (co.status !== 'approved') return;
      if (co.salesOrderId) return;
      if (salesOrderEnsuredRef.current.has(co.id)) return;
      salesOrderEnsuredRef.current.add(co.id);
      ensureChangeOrderSalesOrder(co.id).catch(err => {
        if (co.id) salesOrderEnsuredRef.current.delete(co.id);
        console.warn('[ChangeOrders] auto sales order / QB invoice failed', err);
      });
    });
  }, [changeOrders]);

  const approvedTotal = useMemo(() => sumApprovedChangeOrders(changeOrders), [changeOrders]);
  const currentContractValue = (signedBase ?? 0) + approvedTotal;
  const pendingCount = changeOrders.filter(co => co.status === 'sent').length;

  const flash = useCallback((msg: string) => {
    setFeedback(msg);
    window.setTimeout(() => setFeedback(null), 3500);
  }, []);

  // Contract value a CO layers onto. Approved COs use their approval snapshot
  // (or base + COs approved before them) so later COs never move the numbers
  // the client signed; drafts / new COs layer onto the live contract total.
  const dealValue = useMemo(() => Number(enhancedDeals.find(d => d.id === dealId)?.value) || 0, [enhancedDeals, dealId]);
  const baseFor = useCallback(
    (co?: ChangeOrder | null): number => {
      const base = contractBaseForDeal(signedBase, dealValue, changeOrders);
      if (!co) return base + sumApprovedChangeOrders(changeOrders);
      return priorContractValueForChangeOrder(co, base, changeOrders);
    },
    [changeOrders, signedBase, dealValue],
  );

  // Step 1 of sending: open the review-and-send modal (no email yet).
  const openReviewModal = useCallback(
    (co: ChangeOrder, mode: ChangeOrderReviewSendMode) => {
      if (!co.id) return;
      setReviewSendMode(mode);
      setReviewCo(co);
      setCoRecipients(co.clientEmail || defaultClientEmail ? [co.clientEmail || defaultClientEmail] : []);
      setCoMessage(
        `Hi ${(clientName || co.clientName || '').split(' ')[0] || 'there'},\n\n` +
          `We've prepared a change order for your project, "${co.title}". ` +
          `Please review the details and approve it at your convenience.\n\nThank you!`,
      );
    },
    [defaultClientEmail, clientName],
  );

  const handleSend = useCallback(
    (co: ChangeOrder) => {
      openReviewModal(co, 'release');
    },
    [openReviewModal],
  );

  const handleSendEmailOnly = useCallback(
    (co: ChangeOrder) => {
      openReviewModal(co, 'email_only');
    },
    [openReviewModal],
  );

  // Step 2: actually mark sent, send the email, log activity.
  const handleConfirmSend = useCallback(
    async (message: string, recipients: string[]) => {
      const co = reviewCo;
      if (!co?.id) return;
      setSendingCo(true);
      try {
        const primaryEmail = recipients.find(r => r.includes('@')) || '';
        if (reviewSendMode === 'release') {
          await updateChangeOrder(co.id, {
            status: 'sent',
            sentAt: new Date(),
            ...(primaryEmail ? { clientEmail: primaryEmail } : {}),
          });
        } else if (primaryEmail) {
          await updateChangeOrder(co.id, { clientEmail: primaryEmail });
        }

        let emailOk = false;
        try {
          const resp = await fetch(`${FUNCTIONS_URL}/sendChangeOrderEmail`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ changeOrderId: co.id, customMessage: message, recipients }),
          });
          emailOk = resp.ok;
          if (!resp.ok) {
            const data = await resp.json().catch(() => ({}));
            console.warn('[ChangeOrders] email send failed', data);
          }
        } catch (mailErr) {
          console.warn('[ChangeOrders] email send error', mailErr);
        }

        try {
          const activityTitle =
            reviewSendMode === 'email_only'
              ? `Change order email: ${getChangeOrderReference(co)}`
              : `Change order sent: ${getChangeOrderReference(co)}`;
          const activityDesc =
            reviewSendMode === 'email_only'
              ? `${co.title} — signing link emailed to ${recipients.join(', ')}`
              : `${co.title} (${co.deltaAmount >= 0 ? '+' : ''}$${Math.abs(Math.round(co.deltaAmount)).toLocaleString('en-US')}) sent to ${recipients.join(', ')}`;
          await createActivity({
            type: 'email',
            title: activityTitle,
            description: activityDesc,
            entityType: 'deal',
            entityId: dealId,
            status: 'completed',
            dueDate: new Date(),
            completedAt: new Date(),
            assignedTo: '',
            createdBy: user?.id || 'system',
            actorType: 'employee',
            actorName: user?.name || user?.email || 'Employee',
          });
        } catch (actErr) {
          console.warn('[ChangeOrders] activity log failed', actErr);
        }

        setReviewCo(null);
        if (reviewSendMode === 'email_only') {
          flash(
            emailOk
              ? `Email sent for ${getChangeOrderReference(co)} to ${recipients.join(', ')}.`
              : `Could not send email for ${getChangeOrderReference(co)}. Check Settings → email or use Copy link.`,
          );
        } else {
          flash(
            emailOk
              ? `${getChangeOrderReference(co)} sent to ${recipients.join(', ')}.`
              : `${getChangeOrderReference(co)} marked as sent, but the email could not be delivered. Use “Copy link” to share it manually.`,
          );
        }
      } catch (err) {
        console.error('[ChangeOrders] send failed', err);
        flash('Could not send the change order.');
      } finally {
        setSendingCo(false);
      }
    },
    [reviewCo, reviewSendMode, dealId, user, createActivity, flash],
  );

  const handleRetract = useCallback(
    async (co: ChangeOrder) => {
      if (!co.id || !user) return;
      if (
        !window.confirm(
          `Retract ${getChangeOrderReference(co)} back to draft?\n\n` +
            `It will disappear from the client portal until you release it again. This does not void the change order.`,
        )
      ) {
        return;
      }
      try {
        await retractChangeOrder(co, {
          uid: user.id || 'staff',
          name: user.name || user.email || undefined,
        });
        flash(`${getChangeOrderReference(co)} retracted to draft.`);
      } catch (err) {
        console.error('[ChangeOrders] retract failed', err);
        flash(err instanceof Error ? err.message : 'Could not retract the change order.');
      }
    },
    [user, flash],
  );

  // Mark a draft as sent without sending an email — mirrors the proposal
  // "Mark as sent" action. The client still sees it via the portal / signing link.
  const handleMarkSent = useCallback(
    async (co: ChangeOrder) => {
      if (!co.id) return;
      if (
        !window.confirm(
          `Mark ${getChangeOrderReference(co)} as sent without sending an email? The client will see it in their portal.`,
        )
      ) {
        return;
      }
      setSendingCo(true);
      try {
        await updateChangeOrder(co.id, { status: 'sent', sentAt: new Date() });

        try {
          await createActivity({
            type: 'note',
            title: `Change order marked as sent: ${getChangeOrderReference(co)}`,
            description: `${co.title} (${co.deltaAmount >= 0 ? '+' : ''}$${Math.abs(Math.round(co.deltaAmount)).toLocaleString('en-US')}) marked as sent (no email).`,
            entityType: 'deal',
            entityId: dealId,
            status: 'completed',
            dueDate: new Date(),
            completedAt: new Date(),
            assignedTo: '',
            createdBy: user?.id || 'system',
            actorType: 'employee',
            actorName: user?.name || user?.email || 'Employee',
          });
        } catch (actErr) {
          console.warn('[ChangeOrders] activity log failed', actErr);
        }

        flash(`${getChangeOrderReference(co)} marked as sent. Use “Copy link” to share it.`);
      } catch (err) {
        console.error('[ChangeOrders] mark sent failed', err);
        flash('Could not mark the change order as sent.');
      } finally {
        setSendingCo(false);
      }
    },
    [dealId, user, createActivity, flash],
  );

  const handleCopyLink = useCallback(
    async (co: ChangeOrder) => {
      const link = `${window.location.origin}/change-order/${co.slug}`;
      try {
        await navigator.clipboard.writeText(link);
        flash('Signing link copied to clipboard.');
      } catch {
        flash(`Link: ${link}`);
      }
    },
    [flash],
  );

  const handleVoid = useCallback(
    async (co: ChangeOrder) => {
      if (!co.id) return;
      const wasApproved = co.status === 'approved';
      if (!window.confirm(`Void ${getChangeOrderReference(co)}? ${wasApproved ? 'This will remove its value from the contract.' : ''}`)) {
        return;
      }
      try {
        await updateChangeOrder(co.id, { status: 'void' });
        if (wasApproved) {
          await recomputeAndSyncContractValue(dealId, { updateEnhancedDeal });
        }
        flash(`${getChangeOrderReference(co)} voided.`);
      } catch (err) {
        console.error('[ChangeOrders] void failed', err);
        flash('Could not void the change order.');
      }
    },
    [dealId, updateEnhancedDeal, flash],
  );

  const handleDelete = useCallback(
    async (co: ChangeOrder) => {
      if (!co.id) return;
      if (!window.confirm(`Delete draft ${getChangeOrderReference(co)}? This cannot be undone.`)) return;
      try {
        await deleteChangeOrder(co.id);
        flash('Draft deleted.');
      } catch (err) {
        console.error('[ChangeOrders] delete failed', err);
        flash('Could not delete the draft.');
      }
    },
    [flash],
  );

  const handleInternalApprove = useCallback(
    async (co: ChangeOrder) => {
      if (!co.id || !user || !canInternalApprove) return;
      const ref = getChangeOrderReference(co);
      const delta = `${co.deltaAmount >= 0 ? '+' : ''}$${Math.abs(Math.round(co.deltaAmount)).toLocaleString('en-US')}`;
      if (
        !window.confirm(
          `Client signature is the preferred approval method.\n\n` +
            `Only use internal approval when the client has already agreed offline (email, phone, or in person) and you are recording that agreement on their behalf.\n\n` +
            `Proceed with internal approval for ${ref} — "${co.title}" (${delta})?\n\n` +
            `This will update the contract value and create a sales order without a client signature on file.`,
        )
      ) {
        return;
      }
      setApprovingCoId(co.id);
      try {
        await approveChangeOrderInternally(co.id, {
          userId: user.id || 'staff',
          userName: user.name || user.email || 'Staff',
          isAdmin: true,
          updateEnhancedDeal,
          ensureProcurementTaskGroupForProject,
        });
        flash(`${ref} approved internally. Contract value updated.`);
      } catch (err) {
        console.error('[ChangeOrders] internal approve failed', err);
        const msg = err instanceof Error ? err.message : '';
        flash(
          msg.includes('sales order')
            ? 'Approved, but the sales order / QuickBooks invoice could not be created. Check Won Deals or retry from the change order panel.'
            : 'Could not approve the change order internally.',
        );
      } finally {
        setApprovingCoId(null);
      }
    },
    [user, canInternalApprove, updateEnhancedDeal, ensureProcurementTaskGroupForProject, flash],
  );

  if (!dealId) {
    return (
      <div className="co-empty-state">
        <Building2Placeholder />
        <p>This project has no linked deal — change orders require a deal.</p>
      </div>
    );
  }

  return (
    <div className="co-panel">
      <div className="co-panel-header">
        <div>
          <div className="co-panel-title">Change Orders</div>
          <div className="co-panel-sub">
            {signedBase == null
              ? 'No signed proposal yet — sign the proposal before issuing change orders.'
              : 'Amend the signed contract. Approved change orders update the project value everywhere in the CRM.'}
          </div>
        </div>
        <button
          className="co-btn-primary"
          onClick={() => setEditing(null)}
          disabled={signedBase == null}
          type="button"
        >
          <Plus size={15} /> Create change order
        </button>
      </div>

      <div className="co-summary-strip">
        <div className="co-summary-card">
          <span className="co-summary-card-label">Signed contract</span>
          <span className="co-summary-card-value">{signedBase == null ? '—' : fmt(signedBase)}</span>
        </div>
        <div className="co-summary-card">
          <span className="co-summary-card-label">Approved changes</span>
          <span className={`co-summary-card-value ${approvedTotal < 0 ? 'neg' : approvedTotal > 0 ? 'pos' : ''}`}>
            {approvedTotal === 0 ? '$0' : `${approvedTotal > 0 ? '+' : ''}${fmt(approvedTotal)}`}
          </span>
        </div>
        <div className="co-summary-card">
          <span className="co-summary-card-label">Current contract value</span>
          <span className="co-summary-card-value">{signedBase == null ? '—' : fmt(currentContractValue)}</span>
        </div>
        <div className="co-summary-card">
          <span className="co-summary-card-label">Awaiting signature</span>
          <span className="co-summary-card-value">{pendingCount}</span>
        </div>
      </div>

      {feedback && (
        <div className="co-empty" style={{ borderStyle: 'solid', borderColor: 'var(--pm-accent, #2c3e2f)', color: 'var(--pm-accent, #2c3e2f)' }}>
          {feedback}
        </div>
      )}

      {loading ? (
        <div className="co-empty">Loading change orders…</div>
      ) : changeOrders.length === 0 ? (
        <div className="co-empty-state">
          <ClipboardList size={40} />
          <p>No change orders yet. Create one to amend the signed contract.</p>
        </div>
      ) : (
        <div className="co-list">
          {changeOrders.map(co => {
            const isDraft = co.status === 'draft';
            const isSent = co.status === 'sent';
            const isApproved = co.status === 'approved';
            const isInternal = isInternallyApprovedChangeOrder(co);
            return (
              <div className="co-list-row" key={co.id} data-internal={isInternal ? 'true' : undefined}>
                <span className="co-list-number" title={getChangeOrderReference(co)}>{getChangeOrderReference(co)}</span>
                <div className="co-list-main" style={{ minWidth: 0 }}>
                  <div className="co-list-title">{co.title}</div>
                  {co.scopeDescription && <div className="co-list-scope">{co.scopeDescription}</div>}
                </div>
                <span className={`co-list-amount ${co.deltaAmount < 0 ? 'neg' : 'pos'}`}>
                  {co.deltaAmount >= 0 ? '+' : ''}{fmt(co.deltaAmount)}
                </span>
                <div className="co-list-status">
                  <span className="co-status-pill" data-status={co.status}>{co.status}</span>
                  {isInternal && (
                    <ChangeOrderInternalBadge recordedBy={co.internalApprovedBy} />
                  )}
                </div>
                <div className="co-list-actions">
                  {isDraft && (
                    <>
                      <button className="co-icon-btn" onClick={() => setEditing(co)} title="Edit" aria-label="Edit change order">
                        <Pencil size={15} />
                      </button>
                      <button className="co-icon-btn" onClick={() => handleSend(co)} title="Review & send for signature" aria-label="Review and send for signature">
                        <Send size={15} />
                      </button>
                      <button
                        className="co-icon-btn"
                        onClick={() => handleMarkSent(co)}
                        title="Mark as sent (no email)"
                        aria-label="Mark as sent without sending an email"
                        disabled={sendingCo}
                      >
                        <Check size={15} />
                      </button>
                      {canInternalApprove && (
                        <button
                          className="co-icon-btn"
                          onClick={() => handleInternalApprove(co)}
                          title="Approve internally (no signature)"
                          aria-label="Approve internally without client signature"
                          disabled={!!approvingCoId}
                        >
                          <ShieldCheck size={15} />
                        </button>
                      )}
                      <button className="co-icon-btn danger" onClick={() => handleDelete(co)} title="Delete draft" aria-label="Delete draft">
                        <Trash2 size={15} />
                      </button>
                    </>
                  )}
                  {isSent && (
                    <>
                      <button
                        className="co-icon-btn"
                        onClick={() => handleSendEmailOnly(co)}
                        title="Send email with signing link"
                        aria-label="Send change order email"
                        disabled={sendingCo}
                      >
                        <Mail size={15} />
                      </button>
                      <button className="co-icon-btn" onClick={() => handleCopyLink(co)} title="Copy signing link" aria-label="Copy signing link">
                        <Link2 size={15} />
                      </button>
                      <button
                        className="co-icon-btn"
                        onClick={() => handleRetract(co)}
                        title="Retract to draft (remove from portal)"
                        aria-label="Retract change order to draft"
                      >
                        <Undo2 size={15} />
                      </button>
                      {canInternalApprove && (
                        <button
                          className="co-icon-btn"
                          onClick={() => handleInternalApprove(co)}
                          title="Approve internally (no signature)"
                          aria-label="Approve internally without client signature"
                          disabled={!!approvingCoId}
                        >
                          <ShieldCheck size={15} />
                        </button>
                      )}
                      <button className="co-icon-btn" onClick={() => setEditing(co)} title="View" aria-label="View change order">
                        <FileText size={15} />
                      </button>
                      <button className="co-icon-btn danger" onClick={() => handleVoid(co)} title="Void" aria-label="Void change order">
                        <Ban size={15} />
                      </button>
                    </>
                  )}
                  {isApproved && (
                    <>
                      {co.signedPdfUrl && (
                        <button className="co-icon-btn" onClick={() => window.open(co.signedPdfUrl, '_blank')} title="Signed PDF" aria-label="Open signed PDF">
                          <ExternalLink size={15} />
                        </button>
                      )}
                      <button className="co-icon-btn" onClick={() => setEditing(co)} title="View" aria-label="View change order">
                        <FileText size={15} />
                      </button>
                      <button className="co-icon-btn danger" onClick={() => handleVoid(co)} title="Void" aria-label="Void change order">
                        <Ban size={15} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing !== undefined && (
        <ChangeOrderEditor
          dealId={dealId}
          projectId={projectId}
          clientName={clientName}
          clientEmail={defaultClientEmail}
          projectName={projectName}
          projectNumber={projectNumber}
          existing={editing}
          baseContractValue={baseFor(editing)}
          currentUserUid={user?.id || 'unknown'}
          currentUserName={user?.name || user?.email || undefined}
          onClose={() => setEditing(undefined)}
          onSaved={() => setEditing(undefined)}
          onSend={co => {
            setEditing(undefined);
            handleSend(co);
          }}
        />
      )}

      {reviewCo && (
        <ChangeOrderReviewModal
          open={!!reviewCo}
          onClose={() => { if (!sendingCo) setReviewCo(null); }}
          onSend={handleConfirmSend}
          changeOrder={reviewCo}
          clientName={clientName || reviewCo.clientName || 'Client'}
          priorContractValue={baseFor(reviewCo)}
          sending={sendingCo}
          savedMessage={coMessage}
          onMessageChange={setCoMessage}
          savedRecipients={coRecipients}
          onRecipientsChange={setCoRecipients}
          sendMode={reviewSendMode}
        />
      )}
    </div>
  );
}

function Building2Placeholder() {
  return <ClipboardList size={40} />;
}
